import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Button, Container, IconButton, Card, CircularProgress, Alert } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import DownloadIcon from '@mui/icons-material/Download';
import { useLocation } from 'react-router-dom';

const DocumentViewer = () => {
    const { docUrl } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const rfpIssuer = location.state?.rfpIssuer || "Microsoft RFP Details";
    
    // Extract file name from state or URL
    const extractFileNameFromUrl = (url) => {
        try {
            const urlObj = new URL(url);
            const pathname = urlObj.pathname;
            const segments = pathname.split('/').filter(Boolean);
            const lastSegment = segments[segments.length - 1] || '';
            // Decode the filename and remove query parameters if any
            return decodeURIComponent(lastSegment.split('?')[0]) || 'Document';
        } catch {
            return 'Document';
        }
    };
    
    const [zoom, setZoom] = useState(1);
    const [rotation, setRotation] = useState(0);
    const [loading, setLoading] = useState(true);
    const [iframeError, setIframeError] = useState(false);
    const [urlError, setUrlError] = useState(null);
    const [decodedUrl, setDecodedUrl] = useState('');
    const [iframeKey, setIframeKey] = useState(0); // Key to force iframe reload
    const [fileName, setFileName] = useState(location.state?.fileName || null);
    const [useDirectView, setUseDirectView] = useState(false); // State to use direct document URL instead of viewer service
    
    // Decode and validate URL in useEffect to avoid render-time errors
    useEffect(() => {
        if (!docUrl) {
            setUrlError('Document URL is missing.');
            setLoading(false);
            return;
        }
        
        try {
            // Handle already decoded URLs or encoded URLs
            let url = docUrl;
            
            // Try to decode if it's encoded
            try {
                url = decodeURIComponent(docUrl);
            } catch (e) {
                // If decode fails, use original (might already be decoded)
                url = docUrl;
            }
            
            // Validate URL format
            if (!url || url === 'null' || url === 'undefined' || url.trim() === '') {
                setUrlError('Document URL is missing or invalid.');
                setLoading(false);
                return;
            }
            
            if (!url.startsWith('http://') && !url.startsWith('https://')) {
                setUrlError('Invalid document URL format.');
                setLoading(false);
                return;
            }
            
            // Always properly encode path segments for S3 URLs with spaces and special characters
            // Parse URL manually to ensure proper encoding
            const match = url.match(/^(https?:\/\/)([^\/]+)(\/.*)?$/);
            if (match) {
                const protocol = match[1];
                const host = match[2];
                const path = match[3] || '';
                
                // Split path and encode each segment properly
                // This handles spaces (like "document 3") and special characters (#, parentheses, etc.)
                // But preserves valid URL characters like underscores, hyphens, and dots
                const pathSegments = path.split('/').filter(Boolean);
                const encodedSegments = pathSegments.map(segment => {
                    try {
                        // First decode in case it's partially encoded
                        const decoded = decodeURIComponent(segment);
                        
                        // Only encode characters that need encoding (spaces, special chars)
                        // But preserve valid URL characters like _, -, ., etc.
                        // Use a more conservative encoding approach
                        return decoded.split('').map(char => {
                            // Characters that are safe in URLs and shouldn't be encoded
                            if (/[a-zA-Z0-9._-]/.test(char)) {
                                return char;
                            }
                            // Encode everything else
                            return encodeURIComponent(char);
                        }).join('');
                    } catch {
                        // If decode fails, check if it's already properly encoded
                        // If it contains % and valid hex, it's likely already encoded
                        if (segment.includes('%') && /^[%a-zA-Z0-9._-]+$/.test(segment.replace(/%[0-9A-Fa-f]{2}/g, ''))) {
                            return segment; // Already encoded, use as-is
                        }
                        // Otherwise, encode only what needs encoding
                        return segment.split('').map(char => {
                            if (/[a-zA-Z0-9._-]/.test(char)) {
                                return char;
                            }
                            return encodeURIComponent(char);
                        }).join('');
                    }
                });
                
                // Reconstruct the URL with properly encoded path segments
                url = `${protocol}${host}/${encodedSegments.join('/')}`;
                
                // Validate the properly encoded URL
                try {
                    new URL(url);
                } catch (urlError) {
                    console.warn('URL validation failed after encoding, but proceeding:', url);
                    // Still proceed - the encoding should be correct for S3
                }
            } else {
                // If we can't parse, try to validate as-is
                try {
                    new URL(url);
                } catch (urlError) {
                    console.error('Invalid URL format:', urlError, url);
                    setUrlError('Invalid document URL format.');
                    setLoading(false);
                    return;
                }
            }
            
            setDecodedUrl(url);
            setUrlError(null);
            setLoading(true);
            setIframeError(false);
            setIframeKey(prev => prev + 1); // Force iframe reload with new key
            
            // If fileName is not set from state, extract it from URL
            if (!location.state?.fileName) {
                const extractedFileName = extractFileNameFromUrl(url);
                setFileName(extractedFileName);
            }
        } catch (error) {
            console.error('Error decoding or validating URL:', error);
            setUrlError('Invalid or malformed document URL.');
            setLoading(false);
        }
    }, [docUrl]);
    
    // Get file extension to determine viewer type
    const fileExtension = useMemo(() => {
        if (!decodedUrl) return '';
        try {
            const urlObj = new URL(decodedUrl);
            const pathname = urlObj.pathname.toLowerCase();
            // Extract extension from pathname (handles URLs with query parameters)
            if (pathname.endsWith('.pdf')) return 'pdf';
            if (pathname.endsWith('.docx')) return 'docx';
            if (pathname.endsWith('.doc')) return 'doc';
            // Fallback: check the entire URL
            const urlLower = decodedUrl.toLowerCase();
            if (urlLower.includes('.pdf')) return 'pdf';
            if (urlLower.includes('.docx')) return 'docx';
            if (urlLower.includes('.doc')) return 'doc';
            return 'unknown';
        } catch (e) {
            // If URL parsing fails, check the string directly
            const urlLower = decodedUrl.toLowerCase();
            if (urlLower.includes('.pdf')) return 'pdf';
            if (urlLower.includes('.docx')) return 'docx';
            if (urlLower.includes('.doc')) return 'doc';
            return 'unknown';
        }
    }, [decodedUrl]);
    
    // Generate viewer URL based on file type
    const viewerUrl = useMemo(() => {
        if (!decodedUrl || urlError) return '';
        
        // For PDF files, use Google Docs Viewer (was working before)
        if (fileExtension === 'pdf') {
            return `https://docs.google.com/gview?url=${encodeURIComponent(decodedUrl)}&embedded=true`;
        }
        
        // For DOCX and DOC files, try Microsoft Office Online Viewer first
        if (fileExtension === 'docx' || fileExtension === 'doc') {
            return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(decodedUrl)}`;
        }
        
        // For other files, use Google Docs Viewer as fallback
        return `https://docs.google.com/gview?url=${encodeURIComponent(decodedUrl)}&embedded=true`;
    }, [decodedUrl, fileExtension, urlError]);
    
    useEffect(() => {
        // Reset states when URL changes
        if (decodedUrl && viewerUrl) {
            setLoading(true);
            setIframeError(false);
            setUseDirectView(false); // Reset direct view flag when URL changes
            
            // Give viewer services time to load the document.
            // If onLoad never fires (common when Google Docs viewer can't access the PDF),
            // show our own inline error so users aren't left with a blank white screen.
            const fallbackHideLoading = setTimeout(() => {
                setLoading(prev => {
                    if (prev) {
                        // Still waiting for the iframe → treat as viewer failure
                        setIframeError(true);
                        return false;
                    }
                    return prev;
                });
            }, 18000); // Increased to 18 seconds to give large files more time to load
            
            return () => clearTimeout(fallbackHideLoading);
        }
    }, [decodedUrl, viewerUrl, fileExtension]);
    
    useEffect(() => {
        // Only show error if document truly fails to load after extended period
        // This prevents premature error messages while document is still loading
        if (!urlError && decodedUrl && viewerUrl && !loading) {
            // Only check for errors after loading has stopped
            // Give the document plenty of time to load before showing any error
            const errorCheckTimeout = setTimeout(() => {
                // Only set error if we haven't received any indication of successful load
                // This is a last resort - most documents will load before this
                if (!loading) {
                    // Don't automatically set error - let user retry if needed
                    // The iframe will attempt to load regardless
                }
            }, 60000); // 60 seconds - very long timeout to avoid false errors
            
            return () => clearTimeout(errorCheckTimeout);
        } else if (urlError) {
            setLoading(false);
        }
    }, [loading, urlError, decodedUrl, viewerUrl]);
    
    // Handle opening document in new tab as fallback
    const handleOpenInNewTab = () => {
        if (decodedUrl) {
            window.open(decodedUrl, '_blank');
        }
    };

    const handleZoomChange = (direction) => {
        setZoom(prevZoom => {
            if (direction === 'in') return Math.min(prevZoom + 0.01, 2);
            if (direction === 'out') return Math.max(prevZoom - 0.01, 0.5);
            return prevZoom;
        });
    };

    const handleRotate = () => {
        setRotation(prev => (prev + 90) % 360);
    };

    const handleDownload = () => {
        if (!decodedUrl || urlError) {
            return;
        }
        const link = document.createElement('a');
        link.href = decodedUrl;
        // Use fileName if available, otherwise fallback to rfpIssuer or 'document'
        const downloadName = fileName || rfpIssuer || 'document';
        // Ensure the file extension is included
        const nameWithoutExt = downloadName.replace(/\.(pdf|docx|doc)$/i, '');
        link.download = `${nameWithoutExt}.${fileExtension || 'pdf'}`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };
    
    const handleRetry = () => {
        setIframeError(false);
        setLoading(true);
        setUseDirectView(false); // Reset to try viewer service again
        setIframeKey(prev => prev + 1); // Force iframe reload
    };
    
    // Handle switching to direct view when viewer service fails
    const handleUseDirectView = () => {
        setUseDirectView(true);
        setIframeError(false);
        setLoading(true);
        setIframeKey(prev => prev + 1); // Force iframe reload with direct URL
    };
    
    // Determine the actual URL to use for the iframe
    const actualViewerUrl = useMemo(() => {
        if (!decodedUrl) return '';
        
        // If using direct view (fallback for when viewer services fail), use the document URL directly
        if (useDirectView) {
            return decodedUrl;
        }
        
        // Otherwise use the viewer service URL
        return viewerUrl;
    }, [decodedUrl, useDirectView, viewerUrl]);

    const handleBack = () => {
        // Navigate back, or to dashboard if no history
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            navigate('/dashboard');
        }
    };

    // Show error if URL is invalid
    if (urlError) {
        return (
            <Container sx={{ backgroundColor: '#fff', borderRadius: '8px', padding: '16px', width: '97%', maxWidth: 'none' }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px', alignItems: 'center' }}>
                    <Button variant="outlined" onClick={handleBack} className='border-primary text-primary text-capitalize rounded-3 float-end'>
                        Back
                    </Button>
                </Box>
                <Alert severity="error" sx={{ mt: 2 }}>
                    {urlError} The document may not be available or the URL is corrupted.
                </Alert>
            </Container>
        );
    }

    return (
        <Container sx={{ backgroundColor: '#fff', borderRadius: '8px', padding: '16px', width: '97%', maxWidth: 'none' }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px', alignItems: 'center' }}>
                {/* <Typography variant="h5">{rfpIssuer}</Typography> */}
                <Button variant="outlined" onClick={handleBack} className='border-primary text-primary text-capitalize rounded-3 float-end'>
                    Back
                </Button>
            </Box>

            <Card sx={{ backgroundColor: '#000', padding: '10px', borderRadius: '4px' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h5" sx={{ color: '#FFF', display: 'inline-block', margin: '0 10px' }}>{fileName || rfpIssuer}</Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Button onClick={() => handleZoomChange('out')} sx={{ color: '#FFF' }}>-</Button>
                        <Typography sx={{ color: '#FFF', display: 'inline-block', margin: '0 10px' }}>{Math.round(zoom * 100)}%</Typography>
                        <Button onClick={() => handleZoomChange('in')} sx={{ color: '#FFF' }}>+</Button>

                        <IconButton onClick={handleDownload} sx={{ color: '#FFF', marginLeft: '10px' }}>
                            <DownloadIcon />
                        </IconButton>
                        <IconButton onClick={handleRotate} sx={{ color: '#FFF', marginLeft: '10px' }}>
                            <RotateRightIcon />
                        </IconButton>
                    </Box>
                </Box>
            </Card>

            <Card sx={{ position: 'relative', overflow: 'hidden', borderRadius: '4px', height: '600px' }}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: '#FFF',
                        zIndex: loading ? 10 : -1,
                        opacity: loading ? 1 : 0,
                        transition: 'opacity 0.3s ease',
                        pointerEvents: loading ? 'auto' : 'none',
                    }}
                >
                    {loading && <CircularProgress className='text-primary' />}
                </Box>
                {actualViewerUrl && (
                    <Box
                        sx={{
                            position: 'relative',
                            width: '100%',
                            height: '100%',
                            transform: `rotate(${rotation}deg)`,
                            transition: 'transform 0.3s ease',
                        }}
                    >
                        <iframe
                            key={iframeKey}
                            src={actualViewerUrl}
                            title="Document Viewer"
                            width="100%"
                            height="100%"
                            onLoad={(e) => {
                                // Clear loading state when iframe loads
                                // Add a small delay to ensure content is rendered
                                setTimeout(() => {
                                    setLoading(false);
                                    setIframeError(false);
                                }, 500);
                            }}
                            onError={() => {
                                // onError rarely fires for iframes due to cross-origin restrictions
                                console.warn('Iframe onError event fired');
                                setLoading(false);
                            }}
                            style={{
                                border: 'none',
                                transform: `scale(${zoom})`,
                                transformOrigin: 'top left',
                                width: '100%',
                                height: '100%',
                                backgroundColor: '#fff',
                            }}
                            sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-downloads"
                            allow="fullscreen"
                            loading="eager"
                        />
                    </Box>
                )}
                {/* Show fallback option if viewer service shows "No preview available" (not for PDFs, DOC, or DOCX) */}
                {!useDirectView && fileExtension !== 'pdf' && fileExtension !== 'docx' && fileExtension !== 'doc' && (
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 16,
                            right: 16,
                            zIndex: 20,
                        }}
                    >
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleUseDirectView}
                            sx={{
                                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                color: '#fff',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.9)',
                                },
                            }}
                        >
                            View Directly
                        </Button>
                    </Box>
                )}
                {iframeError && !loading && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            zIndex: 15,
                            padding: 3,
                            textAlign: 'center',
                        }}
                    >
                        <Typography variant="body1" sx={{ mb: 3, maxWidth: 420, color: '#333' }}>
                            This file is too heavy to load or may be a malicious file. Could you please click on the retry button to view your document?
                        </Typography>
                        <Button variant="contained" onClick={handleRetry} className='bg-primary text-white' sx={{ minWidth: 140 }}>
                            Retry
                        </Button>
                    </Box>
                )}
            </Card>
        </Container>
    );
};

export default DocumentViewer;